import { useEffect, useState } from 'react'

import { Checkbox, Modal, TextInput, Tooltip } from '@mantine/core'
import { Mail } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { EXPORT_PIPELINE_ANALYTICS } from 'src/graphql/analyticsExports.graphql'

import type {
  PipelineApplicantFiltersInput,
  AnalyticsExportSheet,
} from 'types/graphql'

// Mirror of api/src/lib/utils/contact.utils.ts:isValidEmail — used here for
// inline disable-on-invalid UX. The backend re-validates as the source of
// truth, so keeping the regex in sync is best-effort only.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

interface ExportAnalyticsButtonProps {
  filters: PipelineApplicantFiltersInput
}

const ExportAnalyticsButton = ({ filters }: ExportAnalyticsButtonProps) => {
  const [opened, setOpened] = useState(false)

  return (
    <>
      <Tooltip label="Email pipeline analytics" position="bottom" withArrow>
        <button
          type="button"
          aria-label="Email pipeline analytics"
          onClick={() => setOpened(true)}
          data-testid="export-analytics-button"
          className={join(
            'inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-doubleNickel-gray-500 transition-colors',
            'hover:border-gray-300 hover:text-doubleNickel-gray-900',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-doubleNickel-brand-500 focus-visible:ring-offset-1'
          )}
        >
          <Mail size={16} />
        </button>
      </Tooltip>

      <ExportAnalyticsModal
        opened={opened}
        onClose={() => setOpened(false)}
        filters={filters}
      />
    </>
  )
}

export default ExportAnalyticsButton

// ---------------------------------------------------------------------------
// Modal (exported for direct rendering in tests + Storybook)
// ---------------------------------------------------------------------------

export interface ExportAnalyticsModalProps {
  opened: boolean
  onClose: () => void
  filters: PipelineApplicantFiltersInput
}

// Display + selection order. The backend orchestrator imposes the same
// canonical order on the workbook regardless of input, but matching it here
// keeps the checkbox list aligned with the resulting tab order so users
// aren't surprised by the workbook layout.
const ALL_SHEETS: ReadonlyArray<{
  value: AnalyticsExportSheet
  label: string
  hint: string
}> = [
  {
    value: 'OVERVIEW',
    label: 'Overview',
    hint: 'KPIs, Pipeline Summary, Pipeline by Job Listing',
  },
  {
    value: 'MARKETING',
    label: 'Marketing',
    hint: 'Per-source funnel + Not Interested / Disqualified reasons',
  },
  {
    value: 'PERFORMANCE',
    label: 'Performance',
    hint: 'Recruiter leaderboard + per-recruiter funnel',
  },
  {
    value: 'APPLICANTS',
    label: 'Applicants',
    hint: 'Full applicant list with stages, dates, lead source',
  },
]

const DEFAULT_SELECTED_SHEETS: AnalyticsExportSheet[] = ALL_SHEETS.map(
  (s) => s.value
)

export const ExportAnalyticsModal = ({
  opened,
  onClose,
  filters,
}: ExportAnalyticsModalProps) => {
  const { currentUser } = useAuth()

  const [selectedSheets, setSelectedSheets] = useState<AnalyticsExportSheet[]>(
    DEFAULT_SELECTED_SHEETS
  )
  const [recipientEmail, setRecipientEmail] = useState<string>(
    currentUser?.email ?? ''
  )

  // Re-prefill the recipient field when the modal opens, so that closing +
  // re-opening it after the user cleared the field restores a sensible default.
  useEffect(() => {
    if (opened) {
      setRecipientEmail(currentUser?.email ?? '')
      setSelectedSheets(DEFAULT_SELECTED_SHEETS)
    }
  }, [opened, currentUser?.email])

  const [exportMutation, { loading }] = useMutation(EXPORT_PIPELINE_ANALYTICS, {
    onCompleted: () => {
      toast('Export queued — check your email shortly.', 'success')
      onClose()
    },
    onError: (error) => {
      toast(error.message || 'Something went wrong, please try again.', 'error')
    },
  })

  const trimmedRecipient = recipientEmail.trim()
  const isRecipientValid = EMAIL_RE.test(trimmedRecipient)
  const hasSheet = selectedSheets.length > 0

  const canSubmit = !loading && hasSheet && isRecipientValid

  const handleSubmit = () => {
    if (!canSubmit) return
    exportMutation({
      variables: {
        input: {
          filters,
          sheets: selectedSheets,
          recipientEmail: trimmedRecipient,
        },
      },
    })
  }

  const toggleSheet = (sheet: AnalyticsExportSheet) => {
    setSelectedSheets((prev) =>
      prev.includes(sheet) ? prev.filter((s) => s !== sheet) : [...prev, sheet]
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Export Pipeline Analytics"
      size="md"
      classNames={{
        title: 'text-lg font-semibold text-doubleNickel-gray-900',
      }}
      data-testid="export-analytics-modal"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-doubleNickel-gray-700">
            Sheets to include
          </p>
          <div className="space-y-2">
            {ALL_SHEETS.map((sheet) => (
              <Checkbox
                key={sheet.value}
                checked={selectedSheets.includes(sheet.value)}
                onChange={() => toggleSheet(sheet.value)}
                data-testid={`export-sheet-checkbox-${sheet.value}`}
                label={
                  <span className="text-sm text-doubleNickel-gray-900">
                    <span className="font-medium">{sheet.label}</span>
                    <span className="ml-1 text-xs text-doubleNickel-gray-500">
                      — {sheet.hint}
                    </span>
                  </span>
                }
              />
            ))}
          </div>
        </div>

        <TextInput
          label="Recipient email"
          placeholder="you@example.com"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.currentTarget.value)}
          error={
            trimmedRecipient.length > 0 && !isRecipientValid
              ? 'Not a valid email address'
              : undefined
          }
          data-testid="export-recipient-input"
        />

        <p className="text-xs text-doubleNickel-gray-500">
          The XLSX will be emailed to the recipient within a minute or two.
          When you email someone else, you&apos;ll be CC&apos;d so you have a
          copy. The exported data matches the date range and filters currently
          active on the page.
        </p>

        <div className="flex justify-end gap-2">
          <DNButton variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </DNButton>
          <DNButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="export-submit-button"
          >
            {loading ? 'Sending…' : 'Export'}
          </DNButton>
        </div>
      </div>
    </Modal>
  )
}
